import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, User, Settings, Shield, Store, Bike, ShoppingBag, Home, ClipboardList, Package, MessageSquare, LayoutDashboard } from "lucide-react";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";

export default function MobileDrawer({ isOpen, onClose, role = "buyer", extraNav = [] }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (to) => {
    onClose();
    navigate(to);
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate("/");
  };

  // Define role-specific navigation links
  const getLinks = () => {
    switch (role) {
      case "buyer":
        return [
          { to: "/app", label: "الرئيسية", icon: Home },
          { to: "/app/orders", label: "طلباتي", icon: ClipboardList },
          { to: "/app/checkout", label: "السلة", icon: ShoppingBag },
          { to: "/app/profile", label: "الملف الشخصي والعناوين", icon: User },
        ];
      case "seller":
        return [
          { to: "/seller", label: "الطلبات الواردة", icon: ClipboardList },
          { to: "/seller", label: "المنتجات", icon: Package, action: () => {} },
          { to: "/seller", label: "إعدادات المتجر", icon: Store },
        ];
      case "driver":
        return [
          { to: "/driver", label: "الطلبات المتاحة", icon: ClipboardList },
          { to: "/driver", label: "توصيلاتي النشطة والمكتملة", icon: Bike },
        ];
      case "admin":
        return [
          { to: "/admin", label: "لوحة التحكم", icon: LayoutDashboard },
          { to: "/admin/users", label: "المستخدمون", icon: User },
          { to: "/admin/shops", label: "المتاجر", icon: Store },
          { to: "/admin/products", label: "المنتجات", icon: Package },
          { to: "/admin/orders", label: "الطلبات", icon: ClipboardList },
          { to: "/admin/whatsapp", label: "ربط واتساب", icon: MessageSquare },
          { to: "/admin/settings", label: "إعدادات النظام", icon: Settings },
        ];
      default:
        return [];
    }
  };

  const links = extraNav.length > 0 ? extraNav : getLinks();

  const roleLabels = {
    buyer: "مشتري (زبون)",
    seller: "بائع (صاحب متجر)",
    driver: "سائق توصيل",
    admin: "مدير النظام (أدمن)"
  };

  const userInitial = user?.name?.trim()?.charAt(0) || "U";

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" dir="rtl" className="w-[300px] sm:w-[340px] p-0 flex flex-col bg-card border-l border-border shadow-2xl z-50">
        {/* Header / User Profile Section */}
        <div className="p-6 brand-panel text-white relative overflow-hidden">
          <div className="flex items-center gap-3.5 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md grid place-items-center text-lg font-bold text-white shadow-inner">
              {userInitial}
            </div>
            <div className="overflow-hidden">
              <h3 className="font-display font-bold text-base truncate leading-tight">{user?.name || "زائر"}</h3>
              <p className="text-xs text-white/80 font-mono truncate mt-0.5">{user?.email || user?.phone || "-"}</p>
              <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white border border-white/20">
                {roleLabels[user?.role || role] || role}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          <div className="px-3 py-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">
            القائمة الرئيسية
          </div>
          {links.map((item, idx) => {
            const Icon = item.icon || Store;
            const active = location.pathname === item.to;
            return (
              <button
                key={idx}
                onClick={() => {
                  if (item.action) item.action();
                  else handleNavigate(item.to);
                }}
                className={`w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-semibold transition-all min-h-[44px] ${
                  active
                    ? "bg-primary text-primary-foreground shadow-sm font-bold"
                    : "text-foreground hover:bg-muted"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0 opacity-80" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Footer / Theme Switcher & Logout */}
        <div className="p-4 border-t border-border bg-muted/20 space-y-3">
          <div className="flex items-center justify-between px-2">
            <span className="text-xs font-semibold text-muted-foreground">مظهر التطبيق</span>
            <ColorThemeSwitcher />
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-3 h-11 text-destructive hover:bg-destructive/10 border-destructive/20 rounded-xl font-bold"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5" />
            <span>تسجيل الخروج</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
